/**
 * Format CNPJ to Brazilian standard: XX.XXX.XXX/XXXX-XX
 * @param cnpj - CNPJ string (with or without formatting)
 * @returns Formatted CNPJ or '-' if invalid
 */
export const formatCNPJ = (cnpj: string | null | undefined): string => {
  if (!cnpj) return '-';

  // Remove any non-digit characters
  const digitsOnly = cnpj.replace(/\D/g, '');

  // Check if it has the correct length
  if (digitsOnly.length !== 14) return cnpj;

  // Format: XX.XXX.XXX/XXXX-XX
  return digitsOnly.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

/**
 * Format CPF to Brazilian standard: XXX.XXX.XXX-XX
 * @param cpf - CPF string (with or without formatting)
 * @returns Formatted CPF or '-' if invalid
 */
export const formatCPF = (cpf: string | null | undefined): string => {
  if (!cpf) return '-';

  // Remove any non-digit characters
  const digitsOnly = cpf.replace(/\D/g, '');

  // Check if it has the correct length
  if (digitsOnly.length !== 11) return cpf;

  // Format: XXX.XXX.XXX-XX
  return digitsOnly.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
};

/**
 * Format CEP to Brazilian standard: XXXXX-XXX
 * @param cep - CEP string (with or without formatting)
 * @returns Formatted CEP or '-' if invalid
 */
export const formatCEP = (cep: string | null | undefined): string => {
  if (!cep) return '-';

  // Remove any non-digit characters
  const digitsOnly = cep.replace(/\D/g, '');

  // Check if it has the correct length
  if (digitsOnly.length !== 8) return cep;

  // Format: XXXXX-XXX
  return digitsOnly.replace(/^(\d{5})(\d{3})$/, '$1-$2');
};

/**
 * Format phone number to Brazilian standard
 * @param phone - Phone string (with or without formatting)
 * @returns Formatted phone or '-' if invalid
 */
export const formatPhone = (phone: string | null | undefined): string => {
  if (!phone) return '-';

  // Remove any non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');

  // Mobile: (XX) XXXXX-XXXX
  if (digitsOnly.length === 11) {
    return digitsOnly.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  }

  // Landline: (XX) XXXX-XXXX
  if (digitsOnly.length === 10) {
    return digitsOnly.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }

  return phone;
};

/**
 * Format currency to Brazilian Real
 * @param value - Numeric value
 * @returns Formatted currency string
 */
export const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';

  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Format date to Brazilian standard (dd/mm/yyyy)
 * @param date - Date string or Date object
 * @returns Formatted date string
 */
export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return '-';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return '-';

  return dateObj.toLocaleDateString('pt-BR');
};

/**
 * Format datetime to Brazilian standard (dd/mm/yyyy hh:mm:ss)
 * @param date - Date string or Date object
 * @returns Formatted datetime string
 */
export const formatDateTime = (date: string | Date | null | undefined): string => {
  if (!date) return '-';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return '-';

  return dateObj.toLocaleString('pt-BR');
};
